import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';

import { Card, CardBody, Progress } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheck,
  faChevronRight,
  faTools,
} from '@fortawesome/free-solid-svg-icons';
import css from 'styled-jsx/css';

import { MAINTENANCE_LOG_ID } from '../../Constants/StaticUUIDs';

import MobileMaintenanceCardDetails from './MobileMaintenanceCardDetails';

const iconDisplayMap = new Map([
  ['upcoming', { color: 'warning-yellow' }],
  ['due', { color: 'active-dm' }],
  ['complete', { color: 'alert-confirmation' }],
]);

const { className: progressClass, styles: progressStyles } = css.resolve`
  div.progress {
    height: 0.5rem;
  }
`;

export default function MobileMaintenanceCard({
  assetShadowSignalsMap,
  checkedInstances,
  handleMobileCardClick,
  instance: [instanceID, instance] = [],
}) {
  const [showCardDetails, toggleCardDetails] = useState(false);

  const isChecked = checkedInstances.has(instanceID);
  const instanceType = useMemo(() => {
    const { due, maintenanceLogId } = instance;
    switch (true) {
      case !!maintenanceLogId && maintenanceLogId !== MAINTENANCE_LOG_ID:
        return 'complete';
      case !due:
        return 'upcoming';
      case due &&
        (!maintenanceLogId || maintenanceLogId === MAINTENANCE_LOG_ID):
        return 'due';
    }
  }, [instanceID]);
  const { color } = iconDisplayMap.get(instanceType) || {};

  const { value: signalValue = 0 } =
    assetShadowSignalsMap.get(instance?.reminder?.trigger?.name) ?? {};

  const now = moment();
  const due = moment(instance?.dueOn);
  const timeDiff = now.diff(due, 'days');
  const timeDiffDisplayText =
    instanceType === 'due' && timeDiff ? 'Days Overdue' : 'Days Left';
  const { type } = instance?.reminder?.trigger ?? {};

  return (
    <Card
      className={`rounded-lg p-2 ${
        isChecked ? 'border border-brand-primary-alert' : ''
      }`}
    >
      <CardBody className="p-0 d-flex flex-nowrap align-items-center text-muted">
        <div
          className="flex-fill"
          role="button"
          tabIndex={0}
          onClick={() => handleMobileCardClick([instanceID, instance])}
          onKeyPress={() => handleMobileCardClick([instanceID, instance])}
        >
          <div className="d-flex align-items-center py-2">
            <FontAwesomeIcon
              className="text-brand-primary"
              icon={checkedInstances.has(instanceID) ? faCheck : faTools}
              size="1x"
            />
            <p className="font-weight-bolder flex-wrap mb-0 ml-2">
              {instance.reminder?.name ?? ''}
            </p>
          </div>
          <hr className="my-2" />
          <div className="d-flex flex-column">
            {type === 'conditional' && instanceType === 'complete' ? (
              <p className={`mt-2 mb-0 text-${color}`}>Service Completed</p>
            ) : type === 'conditional' ? (
              <Progress
                className={`my-2 ${progressClass}`}
                color={color}
                value={
                  instanceType === 'due'
                    ? 100
                    : (signalValue / instance.value) * 100
                }
              />
            ) : (
              <div
                className={`h-100 rounded-lg border border-${color} d-flex flex-column align-items-center justify-content-around`}
              >
                {instanceType === 'complete' ? (
                  <p className={`text-${color} three-quarter-text`}>
                    Service Complete
                  </p>
                ) : (
                  <>
                    <p className={`mb-0 text-${color}`}>{Math.abs(timeDiff)}</p>
                    <p className="three-quarter-text mb-0 text-text-light-gray text-center text-nowrap">
                      {timeDiffDisplayText}
                    </p>
                  </>
                )}
              </div>
            )}
            {progressStyles}
          </div>
        </div>
        <div className="ml-2 d-flex align-items-center justify-content-center">
          <button
            className="btn btn-link p-0 text-decoration-none shadow-none"
            onClick={() => toggleCardDetails(!showCardDetails)}
          >
            <FontAwesomeIcon
              className="text-muted"
              icon={faChevronRight}
              size="lg"
            />
          </button>
        </div>
      </CardBody>
      {showCardDetails ? (
        <MobileMaintenanceCardDetails
          instance={instance}
          instanceType={instanceType}
          isOpen={showCardDetails}
          signalValue={signalValue}
          timeDiff={timeDiff}
          timeDiffDisplayText={timeDiffDisplayText}
          toggleModal={() => toggleCardDetails(!showCardDetails)}
        />
      ) : null}
      <style jsx>{`
        div:active,
        div:focus {
          outline: 0;
          border: none;
          -moz-outline-style: none;
        }
      `}</style>
      <style jsx global>{`
        .border-brand-primary-alert {
          border-width: medium !important;
        }
      `}</style>
    </Card>
  );
}

MobileMaintenanceCard.propTypes = {
  assetShadowSignalsMap: PropTypes.object,
  checkedInstances: PropTypes.object,
  createdAt: PropTypes.string,
  handleMobileCardClick: PropTypes.func,
  instance: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.object])
  ),
};

MobileMaintenanceCard.defaultProps = {
  assetShadowSignalsMap: new Map(),
  checkedInstances: new Map(),
  instance: ['', {}],
};
